import React from 'react';
import { SyllabusItem, TeacherCurriculumSubject } from '../../types';
import { CustomSyllabusResponse } from '../../services/api';

/**
 * Shared curriculum-coverage helpers + bars.
 *
 * "Coverage" = how many syllabus sub-topics the teacher has ticked off for a
 * class, out of the curriculum's total. It is CLASS-level (the same for every
 * student in a class) — the exact numbers a student sees on their chat topic
 * picker. Used on the teacher Curriculum page and on each student's profile so
 * "how much has been covered" reads identically everywhere.
 */

export interface CoverageRow {
  label: string;
  covered: number;
  total: number;
}

/** NCERT coverage per subject: totals from the curriculum tree, ticks matched
 *  by subject key. */
export const computeNcertCoverage = (
  curriculum: TeacherCurriculumSubject[] | null,
  items: SyllabusItem[] | null
): CoverageRow[] => {
  if (!curriculum || curriculum.length === 0 || items === null) return [];
  return curriculum.map((subj) => {
    const total = subj.chapters.reduce((n, c) => n + (c.sub_topics?.length || 0), 0);
    const done = items.filter((i) => i.subject === subj.subject).length;
    return { label: subj.label, covered: total ? Math.min(done, total) : done, total };
  });
};

/** STEM coverage per (custom) sub-subject: ticks for the teacher's own syllabus
 *  carry subject NULL + the chapter title, so we match by chapter title. */
export const computeStemCoverage = (
  syllabus: CustomSyllabusResponse | null,
  items: SyllabusItem[] | null
): CoverageRow[] => {
  if (!syllabus || syllabus.chapters.length === 0 || items === null) return [];
  const doneByChapter = new Map<string, number>();
  items
    .filter((i) => i.subject == null)
    .forEach((i) => {
      const k = (i.chapter_title || '').trim().toLowerCase();
      doneByChapter.set(k, (doneByChapter.get(k) || 0) + 1);
    });
  const order: string[] = [];
  const groups = new Map<string, CoverageRow>();
  syllabus.chapters.forEach((c) => {
    const label = (c.subject || '').trim() || 'STEM';
    const key = label.toLowerCase();
    if (!groups.has(key)) {
      order.push(key);
      groups.set(key, { label, covered: 0, total: 0 });
    }
    const g = groups.get(key)!;
    const total = c.sub_topics?.length || 0;
    const done = doneByChapter.get(c.title.trim().toLowerCase()) || 0;
    g.total += total;
    g.covered += total ? Math.min(done, total) : done;
  });
  return order.map((k) => groups.get(k)!);
};

/** Slim per-subject coverage strip — one bar per subject with covered/total. */
const CoverageBars: React.FC<{
  rows: CoverageRow[];
  title?: string;
  className?: string;
}> = ({ rows, title = 'Class progress', className }) => {
  const visible = rows.filter((r) => r.total > 0);
  if (visible.length === 0) return null;
  return (
    <div className={className ?? 'mb-3 rounded-2xl border border-[#E9E6F4] bg-white p-4'}>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</p>
      <div className="flex flex-col gap-2.5">
        {visible.map((r) => {
          const pct = Math.min(100, Math.round((r.covered / r.total) * 100));
          return (
            <div key={r.label} className="flex items-center gap-3">
              <span
                className="w-36 flex-none text-xs font-semibold leading-snug text-slate-600"
                title={r.label}
              >
                {r.label}
              </span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-violet-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="w-16 flex-none text-right text-xs font-medium text-slate-500">
                {r.covered}/{r.total}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CoverageBars;
